import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { type ComponentProps, type ForwardedRef, forwardRef } from 'react'
import { cn } from '@/helpers/utils'

const buttonVariants = cva(
	'flex cursor-pointer items-center justify-center font-semibold transition focus-visible:shadow-airbnb-focus-visible disabled:cursor-not-allowed aria-disabled:cursor-not-allowed',
	{
		variants: {
			variant: {
				primary:
					'bg-grey-1000 text-neutral-white hover:bg-neutral-black disabled:bg-grey-200 disabled:text-grey-500 aria-disabled:bg-grey-200 aria-disabled:text-grey-500',
				beigePrimary:
					'bg-beige-1000 text-neutral-white hover:bg-neutral-black disabled:bg-beige-200 disabled:text-beige-500 aria-disabled:bg-beige-200 aria-disabled:text-beige-500',
				brand: 'relative overflow-hidden bg-linear-(--gradient-rausch) text-neutral-white disabled:bg-linear-[unset] disabled:bg-tertiary-bg disabled:text-neutral-white aria-disabled:bg-linear-[unset] aria-disabled:bg-tertiary-bg aria-disabled:text-neutral-white',
				secondary:
					'bg-grey-200 text-grey-1000 hover:bg-grey-300 active:bg-grey-300 disabled:bg-grey-200 disabled:text-grey-500 aria-disabled:bg-grey-200 aria-disabled:text-grey-500',
				secondaryOutline:
					'border border-grey-400 font-medium text-grey-1000 disabled:text-grey-500 aria-disabled:text-grey-500',
				beigeSecondary:
					'bg-beige-400 text-beige-1000 disabled:bg-beige-200 disabled:text-beige-500 aria-disabled:bg-beige-200 aria-disabled:text-beige-500',
				icon: 'bg-grey-200 text-grey-1000 hover:bg-grey-300 active:bg-grey-300 disabled:bg-grey-200 disabled:text-grey-500 aria-disabled:bg-grey-200 aria-disabled:text-grey-500',
				tertiary:
					'text-grey-1000 hover:text-grey-1000 disabled:text-grey-500 aria-disabled:text-grey-500',
				tertiaryUnderlined:
					'text-grey-1000 underline hover:text-grey-1000 disabled:text-grey-500 aria-disabled:text-grey-500'
			},
			size: {
				sm: 'h-8 gap-1 px-4 text-xs/4 [&>span]:gap-1 [&>svg]:size-4',
				md: 'h-10 gap-1 px-5 text-sm/4.5 [&>span]:gap-1 [&>svg]:size-4',
				lg: 'h-12 gap-2.5 px-6 text-md/5 [&>span]:gap-2.5 [&>svg]:size-5',
				xl: 'h-13 gap-2.5 px-6 py-3.5 text-md/6 [&>span]:gap-2.5 [&>svg]:size-6'
			},
			// A rectangle's corner is set per size in `compoundVariants` below,
			// not here: the 32px `sm` button draws a smaller one than the rest.
			shape: {
				rectangle: '',
				square: 'flex-shrink-0 rounded-full',
				rounded: 'rounded-full'
			}
		},
		compoundVariants: [
			// The rectangle's corner, one entry per size. Spelled out rather than
			// defaulted on the shape and overridden for `sm`, because that would
			// emit two `rounded-*` classes and `cn` cannot drop the loser:
			// tailwind-merge does not recognise these theme-token names as border
			// radii, so it keeps both and the stylesheet's order decides. Exactly
			// one radius class is emitted this way.
			{
				size: 'sm',
				shape: 'rectangle',
				class: 'rounded-button-sm'
			},
			{
				size: 'md',
				shape: 'rectangle',
				class: 'rounded-button'
			},
			{
				size: 'lg',
				shape: 'rectangle',
				class: 'rounded-button'
			},
			{
				size: 'xl',
				shape: 'rectangle',
				class: 'rounded-button'
			},
			{
				size: 'sm',
				shape: 'square',
				class: 'size-8 p-0'
			},
			{
				size: 'md',
				shape: 'square',
				class: 'size-10 p-0'
			},
			{
				size: 'lg',
				shape: 'square',
				class: 'size-12 p-0'
			},
			{
				size: 'xl',
				shape: 'square',
				class: 'size-13 p-0'
			}
		],
		defaultVariants: {
			variant: 'primary',
			size: 'md',
			shape: 'rectangle'
		}
	}
)

export type ButtonProps = ComponentProps<'button'> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean
	}

const Button = forwardRef(function Button(
	{ className, variant, size, shape, asChild = false, ...props }: ButtonProps,
	ref: ForwardedRef<HTMLButtonElement>
) {
	const Comp = asChild ? Slot : 'button'

	return (
		<Comp
			className={cn(buttonVariants({ variant, size, shape, className }))}
			data-slot='button'
			ref={ref}
			{...props}
		/>
	)
})

export { Button, buttonVariants }
